import { Fragment, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { getFeeRates, createFeeRate } from '../../services/FeeRateService'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'

const EMPTY_FORM = {
    ratePercent:        '',
    capPerTransaction:  '',
    taxRatePercent:     '',
    effectiveFrom:      '',
}

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day:    '2-digit',
        month:  'short',
        year:   'numeric',
        hour:   '2-digit',
        minute: '2-digit',
    })

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatPercent = (rate) => `${(parseFloat(rate) * 100).toFixed(2)}%`

const StatusBadge = ({ feeRate }) => {
    if (feeRate.is_currently_active) {
        return <span className="badge badge-rounded badge-success">Active</span>
    }
    if (new Date(feeRate.effective_from) > new Date()) {
        return <span className="badge badge-rounded badge-warning">Scheduled</span>
    }
    return <span className="badge badge-rounded badge-secondary">Superseded</span>
}

const FeeRates = () => {
    const queryClient = useQueryClient()
    const userRole = useSelector((state) => state.auth.authUser?.role_slug)

    const [showCreateModal, setShowCreateModal] = useState(false)
    const [form, setForm]                       = useState(EMPTY_FORM)

    const { data, isPending, isError } = useQuery({
        queryKey: ['fee-rates'],
        queryFn:  getFeeRates,
        staleTime: 5 * 60 * 1000,
        enabled:  userRole === ROLE_SLUGS.EXEC_ADMIN,
    })

    const feeRates = data?.data?.data ?? []

    const createMutation = useMutation({
        mutationFn: (payload) => createFeeRate(payload),
        onSuccess: async () => {
            toast.success('New fee rate scheduled successfully.')
            await queryClient.invalidateQueries(['fee-rates'])
            setShowCreateModal(false)
            setForm(EMPTY_FORM)
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to create fee rate.'
            toast.error(message)
        },
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        createMutation.mutate({
            rate:                String(parseFloat(form.ratePercent) / 100),
            cap_per_transaction: parseInt(form.capPerTransaction, 10),
            tax_rate:            String(parseFloat(form.taxRatePercent) / 100),
            effective_from:      form.effectiveFrom,
        })
    }

    const handleClose = () => {
        setShowCreateModal(false)
        setForm(EMPTY_FORM)
    }

    if (userRole !== ROLE_SLUGS.EXEC_ADMIN) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex align-items-center gap-2">
                            <h4 className="card-title mb-0">Fee Rates</h4>
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(true)}
                                className="btn btn-sm btn-primary ms-auto d-none d-lg-inline-flex align-items-center"
                            >
                                <i className="fa fa-plus me-1" aria-hidden="true" />
                                Schedule New Rate
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(true)}
                                className="btn btn-primary rounded-circle ms-auto d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                                title="Schedule new rate"
                                aria-label="Schedule new rate"
                            >
                                <i className="fas fa-plus" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : isError ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load fee rates. Please try again.
                                </p>
                            ) : feeRates.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No fee rates found.
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Platform Rate</th>
                                                    <th scope="col">Cap per Transaction</th>
                                                    <th scope="col">VAT Rate</th>
                                                    <th scope="col">Effective From</th>
                                                    <th scope="col">Created By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {feeRates.map((feeRate) => (
                                                    <tr key={feeRate.id}>
                                                        <td><StatusBadge feeRate={feeRate} /></td>
                                                        <td className="text-nowrap fw-semibold">{formatPercent(feeRate.rate)}</td>
                                                        <td className="text-nowrap">{formatAmount(feeRate.cap_per_transaction)}</td>
                                                        <td className="text-nowrap">{formatPercent(feeRate.tax_rate)}</td>
                                                        <td className="text-nowrap">{formatDate(feeRate.effective_from)}</td>
                                                        <td style={{ overflowWrap: 'anywhere' }}>{feeRate.created_by ?? '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Rate</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Created By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {feeRates.map((feeRate) => (
                                                    <tr key={feeRate.id}>
                                                        <td>
                                                            <div className="fw-semibold">
                                                                {formatPercent(feeRate.rate)} platform, {formatPercent(feeRate.tax_rate)} VAT
                                                            </div>
                                                            <div className="small text-muted">
                                                                Cap {formatAmount(feeRate.cap_per_transaction)}
                                                            </div>
                                                            <div className="small text-muted mt-1">
                                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                                Effective {formatDate(feeRate.effective_from)}
                                                            </div>
                                                        </td>
                                                        <td><StatusBadge feeRate={feeRate} /></td>
                                                        <td style={{ overflowWrap: 'anywhere' }}>{feeRate.created_by ?? '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-group list-group-flush d-block d-md-none">
                                        {feeRates.map((feeRate) => (
                                            <li key={feeRate.id} className="list-group-item py-3">
                                                <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                                                    <span className="fw-semibold text-body">
                                                        {formatPercent(feeRate.rate)} platform
                                                    </span>
                                                    <StatusBadge feeRate={feeRate} />
                                                </div>
                                                <div className="small text-muted">
                                                    VAT {formatPercent(feeRate.tax_rate)}
                                                </div>
                                                <div className="small text-muted">
                                                    Cap {formatAmount(feeRate.cap_per_transaction)}
                                                </div>
                                                <div className="small text-muted mt-1">
                                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                    Effective {formatDate(feeRate.effective_from)}
                                                </div>
                                                {feeRate.created_by && (
                                                    <div className="small text-muted">
                                                        <i className="bi bi-person me-1" aria-hidden="true" />
                                                        {feeRate.created_by}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showCreateModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Schedule New Fee Rate</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">
                        The new rate will take effect from the date and time you set below. The current rate remains active until then.
                    </p>
                    <form id="create-fee-rate-form" onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">
                                Platform Rate (%) <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="e.g. 1.5"
                                min="0"
                                max="100"
                                step="0.001"
                                value={form.ratePercent}
                                onChange={(e) => setForm({ ...form, ratePercent: e.target.value })}
                                required
                            />
                            <div className="form-text">Percentage charged on each transaction (e.g. 1.5 for 1.5%).</div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Cap per Transaction (KES) <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="e.g. 150"
                                min="0"
                                step="1"
                                value={form.capPerTransaction}
                                onChange={(e) => setForm({ ...form, capPerTransaction: e.target.value })}
                                required
                            />
                            <div className="form-text">Maximum fee charged regardless of transaction size.</div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                VAT Rate (%) <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="e.g. 16"
                                min="0"
                                max="100"
                                step="0.001"
                                value={form.taxRatePercent}
                                onChange={(e) => setForm({ ...form, taxRatePercent: e.target.value })}
                                required
                            />
                            <div className="form-text">VAT applied on the platform fee (e.g. 16 for 16%).</div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Effective From <span className="text-danger">*</span>
                            </label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={form.effectiveFrom}
                                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                                required
                            />
                            <div className="form-text">Must be now or a future date and time.</div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={createMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="create-fee-rate-form"
                        variant="primary"
                        disabled={
                            createMutation.isPending ||
                            !form.ratePercent ||
                            !form.capPerTransaction ||
                            !form.taxRatePercent ||
                            !form.effectiveFrom
                        }
                    >
                        {createMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : (
                            'Save Rate'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default FeeRates
